/**
 * Cognito hosted sign-in — authorization code flow with PKCE.
 *
 * PKCE rather than the implicit flow, and no client secret: this is a browser application, so a
 * secret could not be kept, and the implicit flow puts tokens in the URL fragment where browser
 * history and referrers can capture them. PKCE binds the authorization code to a verifier that never
 * leaves this tab.
 *
 * **Tokens are held in memory only**, never localStorage (see auth.ts). A page reload therefore needs
 * a fresh token — but Cognito's hosted UI keeps its own session cookie, so that is a silent redirect,
 * not a password prompt. The trade is deliberate: an XSS payload can read localStorage, and this
 * application displays PHI.
 *
 * The PKCE verifier and state are the only values written to sessionStorage, because they must
 * survive the redirect round-trip to Cognito and back. Both are single-use and cleared on return.
 */

import { authHeaders, NetworkError, setAccessToken } from './auth';

interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
}

export function cognitoConfig(): CognitoConfig | null {
  const domain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
  if (!domain || !clientId) return null;
  const origin = window.location.origin;
  return {
    domain,
    clientId,
    redirectUri: `${origin}/auth/callback`,
    logoutUri: `${origin}/`,
  };
}

export function authMode(): 'cognito' | 'dev' {
  return import.meta.env.VITE_AUTH_MODE === 'cognito' ? 'cognito' : 'dev';
}

const VERIFIER_KEY = 'scribe.pkce.verifier';
const STATE_KEY = 'scribe.pkce.state';
const RETURN_KEY = 'scribe.pkce.return';

/**
 * `signIn()` has several independent callers that can all fire on the same load — a 401 from
 * `api.ts`, a 401 from `review.ts`, and `SilentSignIn`'s own effect all call it, and normally do,
 * since a fresh reload has no in-memory token and everything 401s at once. Without this guard,
 * a second call overwrites the first call's verifier/state in sessionStorage while the first is
 * still mid-flight (the `await challengeFor` below), so whichever redirect actually lands can
 * carry a `state` that no longer matches what's stored — exactly the "Sign-in response did not
 * match this browser session" error a user saw on every plain refresh (2026-09-14). Only the
 * first caller per page load should actually redirect; the rest are the same intent, redundantly
 * signalled.
 */
let signInInFlight = false;

function randomString(bytes = 32): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64Url(buffer);
}

function base64Url(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(digest);
}

/** Redirect to the hosted sign-in page. */
export async function signIn(): Promise<void> {
  if (signInInFlight) return;
  signInInFlight = true;

  const config = cognitoConfig();
  if (!config) {
    signInInFlight = false;
    throw new Error('Cognito is not configured for this build.');
  }

  const verifier = randomString(48);
  // `state` defends the callback against CSRF: a callback whose state does not match what this tab
  // issued is rejected rather than exchanged.
  const state = randomString(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search);

  let challenge: string;
  try {
    challenge = await challengeFor(verifier);
  } catch (err) {
    signInInFlight = false;
    throw err;
  }
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`https://${config.domain}/oauth2/authorize?${params}`);
  // signInInFlight deliberately stays true: navigation is under way, and nothing on this page
  // should start a second one before it lands.
}

/**
 * Complete sign-in on /auth/callback. Returns the path to return to, or null if this is not a
 * callback.
 *
 * Consumes the PKCE verifier/state exactly once — they're deleted from sessionStorage as soon as
 * they're read (single-use, so a replayed callback URL can't reuse them). That makes this
 * function non-idempotent, which collided with React StrictMode's dev-only double-invocation of
 * effects (main.tsx): the first invocation would consume the keys and succeed, the second would
 * find them already gone and throw "did not match this browser session" — which, since a
 * callback failure retries via SilentSignIn (App.tsx), redirected to Cognito again and looped
 * forever. Only reachable in `vite dev` (StrictMode's double-invoke is stripped from production
 * builds), which is exactly why it surfaced only once the frontend started running locally
 * (2026-09-14). Deduplicated the same way as `signIn()` below: concurrent callers share one
 * in-flight promise instead of each re-running the body.
 */
export function handleCallback(): Promise<string | null> {
  if (window.location.pathname !== '/auth/callback') return Promise.resolve(null);
  if (!callbackPromise) callbackPromise = doHandleCallback();
  return callbackPromise;
}

let callbackPromise: Promise<string | null> | null = null;

async function doHandleCallback(): Promise<string | null> {
  const config = cognitoConfig();
  if (!config) throw new Error('Cognito is not configured for this build.');

  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (error) {
    throw new Error(params.get('error_description') || error);
  }

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) || '/';

  // Single-use: cleared before the exchange, so a replayed callback URL cannot reuse them.
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  if (!code || !state || !verifier || state !== expectedState) {
    throw new Error('Sign-in response did not match this browser session. Please sign in again.');
  }

  const response = await fetch(`https://${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  });
  if (!response.ok) {
    throw new Error(`Token exchange failed (${response.status}).`);
  }
  const tokens = await response.json();
  // The *access* token, not the ID token: the API verifies token_use === 'access' and refuses an ID
  // token, which is minted for the client rather than for API authorization.
  setAccessToken(tokens.access_token);
  // In memory only, same as the access token (see the file comment) — kept so `authorizedFetch`
  // below can renew the access token in place instead of falling back to a full page navigation
  // through Cognito every time it expires.
  refreshToken = tokens.refresh_token ?? null;

  // Strip the code from the address bar so it is not left in history.
  window.history.replaceState({}, '', returnTo);
  return returnTo;
}

let refreshToken: string | null = null;

/**
 * Renews the access token in place via the refresh token, with no navigation and no lost
 * component state — unlike `signIn()`'s redirect, which reloads the whole page. Access tokens are
 * deliberately short-lived (60 min — reviewers work on shared clinical workstations, §9.6), but
 * nothing previously *used* the refresh token Cognito already issues alongside it, so every expiry
 * fell through to a full redirect regardless of how "silent" it was — round-tripping through
 * Cognito reloads the SPA, which loses the current review position (window, selection, scroll).
 * Found 2026-09-14: reported as "signs out after an hour or two."
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Coalesces concurrent refreshes (DESIGN.md §13.6 A8).
 *
 * A token expiring while the review screen has several requests in flight — which is the normal
 * case, since a pan issues a manifest, several range fetches and a beat-marker call together —
 * produced one refresh per 401. Each one spends the *same* refresh token, and each response
 * overwrites `accessToken`, so the last to land wins and the others' tokens are discarded. With
 * refresh-token rotation enabled that is worse than wasteful: the second exchange presents a
 * token the first has already invalidated, and the user is signed out by their own app's
 * concurrency.
 *
 * One promise, shared by every caller that arrives while it is running.
 */
export function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const config = cognitoConfig();
  if (!config || !refreshToken) return false;
  try {
    const response = await fetch(`https://${config.domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.clientId,
        refresh_token: refreshToken,
      }),
    });
    if (!response.ok) return false;
    const tokens = await response.json();
    setAccessToken(tokens.access_token);
    // Cognito returns a new refresh token when rotation is enabled, and not otherwise. Keeping
    // the old one when none comes back is correct; dropping it would sign the user out at the
    // next expiry for no reason.
    if (tokens.refresh_token) refreshToken = tokens.refresh_token;
    return true;
  } catch {
    return false;
  }
}

//: Every `fetch` here used to have no timeout at all — the browser's own connection timeout is
//: the only limit, which is minutes, not seconds. A stalled local connection or a dev-proxy hop
//: that never got a response therefore just hung, indistinguishable from a slow analysis: no
//: error, no way to tell the two apart, "over a minute" and counting (2026-09-15). 20s is well
//: above any real request here — the slowest is a manifest lookup, not signal transfer, since the
//: byte ranges it points to are fetched in parallel and each is at most a couple hundred KB.
const REQUEST_TIMEOUT_MS = 20_000;

/**
 * `caller`'s own `AbortSignal`, if any, combined with a fresh timeout — aborting either aborts the
 * fetch. Kept separate from `caller` so `timedOut()` can tell a timeout apart from the caller
 * cancelling the request itself (a superseded pan, an unmounted component): only a timeout should
 * ever be retried.
 */
function withTimeout(caller: AbortSignal | undefined, ms: number) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, ms);
  const onCallerAbort = () => controller.abort();
  if (caller?.aborted) controller.abort();
  else caller?.addEventListener('abort', onCallerAbort);
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    cleanup: () => {
      clearTimeout(timer);
      caller?.removeEventListener('abort', onCallerAbort);
    },
  };
}

/**
 * The one place that decides what to do about a 401: try a silent refresh first, retry the
 * request once if that worked, and only fall back to `signIn()`'s full redirect if the refresh
 * token itself is gone or expired. `api.ts`, `review.ts` and `signal.ts` all call this instead of
 * a bare `fetch` — previously each hit `signIn()` directly on any 401, which is also what
 * `signal.ts`'s waveform fetches never did at all (a token expiring mid-scrub just failed with no
 * recovery attempt).
 *
 * Also the one place that decides what to do about the request never completing at all. A single
 * attempt that times out, or fails before reaching the network (DNS, TLS, a reset connection), is
 * retried once — GET/HEAD only, since those are safe to repeat blindly and everything else here
 * (locks, review actions) either has its own idempotency story or must not be resent unattended.
 * A caller-cancelled request (its own `signal` aborted — a superseded pan, an unmounted component)
 * is never retried; it was cancelled on purpose. Exhausting the retry raises `NetworkError` rather
 * than the browser's bare `TypeError: Failed to fetch`, which read as an auth failure on the
 * sign-in screen and gave no hint that the network path was the thing to check.
 */
export async function authorizedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const retryable = method === 'GET' || method === 'HEAD';
  const callerSignal = init.signal ?? undefined;

  const once = async (): Promise<Response> => {
    const timeout = withTimeout(callerSignal, REQUEST_TIMEOUT_MS);
    try {
      return await fetch(input, {
        ...init,
        signal: timeout.signal,
        headers: { ...authHeaders(), ...(init.headers ?? {}) },
      });
    } catch (err) {
      if (callerSignal?.aborted) throw err; // deliberate cancellation — let it propagate as-is
      if (timeout.timedOut()) throw new NetworkError('Request timed out. Check your connection and try again.');
      throw new NetworkError();
    } finally {
      timeout.cleanup();
    }
  };

  const attempt = async (): Promise<Response> => {
    try {
      return await once();
    } catch (err) {
      if (retryable && !callerSignal?.aborted && err instanceof NetworkError) {
        return once();
      }
      throw err;
    }
  };

  let res = await attempt();
  if (res.status === 401 && authMode() === 'cognito') {
    if (await refreshAccessToken()) {
      res = await attempt();
    }
    if (res.status === 401) {
      void signIn();
    }
  }
  return res;
}

export function signOut(): void {
  setAccessToken(null);
  refreshToken = null;
  const config = cognitoConfig();
  if (!config) {
    window.location.assign('/');
    return;
  }
  const params = new URLSearchParams({ client_id: config.clientId, logout_uri: config.logoutUri });
  window.location.assign(`https://${config.domain}/logout?${params}`);
}
